import {
  ref,
  set,
  onValue,
  onDisconnect,
  serverTimestamp,
  get,
  update
} from 'firebase/database';
import firebaseConfig from '../../config';

const { databaseId } = firebaseConfig;

// Presence timeout threshold (30 seconds)
// Users are considered offline if their lastUpdate is older than this
const PRESENCE_TIMEOUT = 30000; // 30 seconds in milliseconds

// Helper function to get current date key in YYYY-MM-DD format
const getCurrentDateKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

// Helper function to prune old session history (keep last 30 days)
const pruneOldSessionHistory = (sessionHistory) => {
  if (!sessionHistory) return {};

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const pruned = {};

  Object.entries(sessionHistory).forEach(([dateKey, data]) => {
    const dateTimestamp = new Date(dateKey).getTime();
    if (dateTimestamp >= thirtyDaysAgo) {
      pruned[dateKey] = data;
    }
  });

  return pruned;
};

function PresenceFactory(firebase) {
  const setUserOnline = async ({
    sessionId,
    uid,
    name,
    isNewVisit = false,
    existingTotalSessionTime,
    existingTotalVisits
  }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);

    // Use passed data or read from database to preserve totalSessionTime and visit count across devices
    let existingTotalTime;
    let existingVisitCount;
    let existingSessionHistory;
    let existingData;

    if (
      existingTotalSessionTime !== undefined &&
      existingTotalVisits !== undefined
    ) {
      // Use provided data (avoid database read to prevent race conditions)
      existingTotalTime = existingTotalSessionTime;
      existingVisitCount = existingTotalVisits;
      // Need to read sessionHistory from database even if other data is provided
      existingData = await get(userRef);
      existingSessionHistory = existingData.exists()
        ? existingData.val().sessionHistory || {}
        : {};
    } else {
      // Read from database for fresh users
      existingData = await get(userRef);
      existingTotalTime = existingData.exists()
        ? existingData.val().totalSessionTime || 0
        : 0;
      existingVisitCount = existingData.exists()
        ? existingData.val().totalVisits || 0
        : 0;
      existingSessionHistory = existingData.exists()
        ? existingData.val().sessionHistory || {}
        : {};
    }

    // Calculate new visit count
    const newVisitCount = isNewVisit
      ? existingVisitCount + 1
      : existingVisitCount;

    // Initialize or update today's session history entry
    const dateKey = getCurrentDateKey();
    if (isNewVisit) {
      if (!existingSessionHistory[dateKey]) {
        existingSessionHistory[dateKey] = {
          lastActive: serverTimestamp(),
          totalSeconds: 0,
          visitCount: 1
        };
      } else {
        existingSessionHistory[dateKey].lastActive = serverTimestamp();
        existingSessionHistory[dateKey].visitCount += 1;
      }
    }

    // Set user online, preserving existing data and updating visit info
    await set(userRef, {
      currentVisitStart: isNewVisit
        ? serverTimestamp()
        : existingData && existingData.exists()
          ? existingData.val().currentVisitStart || serverTimestamp()
          : serverTimestamp(),
      isOnline: true,
      lastUpdate: serverTimestamp(),
      name,
      sessionHistory: pruneOldSessionHistory(existingSessionHistory),
      sessionStart: serverTimestamp(),
      totalSessionTime: existingTotalTime,
      totalVisits: newVisitCount
    });

    // Automatically mark user offline when disconnected, preserving session data
    onDisconnect(userRef).update({
      isOnline: false,
      lastUpdate: serverTimestamp()
    });

    return userRef;
  };

  const updateSessionTime = async ({
    sessionId,
    uid,
    totalSessionTime,
    sessionTimeDelta = 0
  }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);
    const dateKey = getCurrentDateKey();

    // Read current session history
    const snapshot = await get(userRef);
    const sessionHistory = snapshot.exists()
      ? snapshot.val().sessionHistory || {}
      : {};

    // Update today's bucket
    if (!sessionHistory[dateKey]) {
      sessionHistory[dateKey] = {
        lastActive: serverTimestamp(),
        totalSeconds: sessionTimeDelta,
        visitCount: 1
      };
    } else {
      sessionHistory[dateKey] = {
        ...sessionHistory[dateKey],
        totalSeconds:
          (sessionHistory[dateKey].totalSeconds || 0) + sessionTimeDelta,
        lastActive: serverTimestamp()
      };
    }

    await update(userRef, {
      isOnline: true,
      lastUpdate: serverTimestamp(),
      sessionHistory: pruneOldSessionHistory(sessionHistory),
      totalSessionTime
    });

    // Re-establish disconnect handling after update
    onDisconnect(userRef).update({
      isOnline: false,
      lastUpdate: serverTimestamp()
    });
  };

  const setUserOffline = async ({ sessionId, uid, totalSessionTime }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);

    // Cancel any pending onDisconnect operations
    onDisconnect(userRef).cancel();

    // Update with final session time but preserve the entry
    await update(userRef, {
      isOnline: false,
      totalSessionTime,
      lastUpdate: serverTimestamp()
    });
  };

  const subscribeToSessionPresence = ({ sessionId, callback }) => {
    const presenceRef = ref(firebase.rtdb, `userPresence/${sessionId}`);

    return onValue(presenceRef, (snapshot) => {
      const presenceData = snapshot.val();
      const onlineUsers = [];
      const now = Date.now();

      if (presenceData) {
        Object.entries(presenceData).forEach(([uid, userData]) => {
          if (userData && userData.isOnline) {
            // Check if lastUpdate is recent (within PRESENCE_TIMEOUT)
            const lastUpdate = userData.lastUpdate || 0;
            const timeSinceUpdate = now - lastUpdate;

            // Only include users who have updated within the timeout threshold
            if (timeSinceUpdate <= PRESENCE_TIMEOUT) {
              onlineUsers.push({
                id: uid,
                ...userData
              });
            }
          }
        });
      }

      callback(onlineUsers);
    });
  };

  const monitorConnection = (callback) => {
    const connectedRef = ref(firebase.rtdb, '.info/connected');
    return onValue(connectedRef, (snapshot) => {
      const isConnected = snapshot.val();
      callback(isConnected);
    });
  };

  const getUserPresence = async ({ sessionId, uid }) => {
    const userRef = ref(firebase.rtdb, `userPresence/${sessionId}/${uid}`);
    const snapshot = await get(userRef);
    return snapshot.val();
  };

  return {
    setUserOnline,
    updateSessionTime,
    setUserOffline,
    subscribeToSessionPresence,
    monitorConnection,
    getUserPresence
  };
}

export default PresenceFactory;
